import {
	ReactElement,
	useEffect,
	useState
} from "react";
import {
	CopyControl,
	Dialog,
	Form,
	FormItem,
	Toggle
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import {
	AppLinkViewModel,
	get,
	create,
	deleteRequest
} from "client/Api/Apps/AppLinks/appLink";
import { AlertManager, useAlerts } from "client/Hooks";
import styles from "./AppLinkDialog.module.less";
import { NoUniqueInputIdContext } from "client/Contexts/UniqueInputIdContext";

export type AppLinkDialogProps = {
	onClose(): void;
	environmentAppId: number;
};

export const AppLinkDialog = (props: AppLinkDialogProps): ReactElement => {
	const {
		onClose,
		environmentAppId
	} = props;

	const [appLinkValue, setAppLinkValue] = useState<string>("");
	const [switchValue, setSwitchValue] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);
	const alertManager: AlertManager = useAlerts();

	useEffect(
		() => {
			const cancellation = new AbortController();
			(async function(): Promise<void | (() => void)> {
				setIsLoading(true);
				const signal: AbortSignal = cancellation.signal;

				try {
					const viewModel: AppLinkViewModel = await get(environmentAppId, signal);

					setAppLinkValue(viewModel.url ?? "");
					const isEnabled = Boolean(viewModel.url);
					setSwitchValue(isEnabled);

					setIsLoading(false);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);

					alertManager.showError(localize("_FailedToLoadTheAppLinkSettings"));

					onClose();
				}

				return () => cancellation.abort();
			}());
		},
		[alertManager, environmentAppId, onClose]
	);

	async function onChangeHandler(newValue: boolean): Promise<void> {
		setIsLoading(true);

		try {
			setSwitchValue(newValue);
			if (newValue) {
				const appLink: string = await create(environmentAppId);
				setAppLinkValue(appLink);
			} else {
				await deleteRequest(environmentAppId);
				setAppLinkValue("");
			}
			setIsLoading(false);
		} catch(e) {
			console.error(e);
			const message = switchValue
				? localize("_FailedToEnableTheLink")
				: localize("_FailedToDisableTheLink");

			alertManager.showError(message);

			setIsLoading(false);
		}
	}

	return (
		<Dialog
			hasBusyIndicator={isLoading}
			trackingId="app-link-dialog"
		>
			<Dialog.Header
				title={localize("_CreateAppLink")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<p>{localize("_YouCanCreateAppLinkToDirectlyAccessThisApplicationEverywhere")}</p>
				<Form onSubmit="NONE">
					<FormItem>
						<div className={styles.content}>
							<div className={styles.copyControl}>
								<CopyControl
									value={appLinkValue ?? ""}
									trackingId="app-link"
									buttonKind="primary"
								/>
							</div>
							<div>
								<NoUniqueInputIdContext>
									<Toggle
										value={switchValue}
										onChange={(newValue) => onChangeHandler(newValue)}
										text={localize("_Enable")}
										trackingId="switch"
									/>
								</NoUniqueInputIdContext>
							</div>
						</div>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer />
		</Dialog>
	);
};
AppLinkDialog.displayName = "AppLinkDialog";
